import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Instagram, Facebook, Send } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { CONTACT } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Pricing", href: "#pricing" },
  { label: "Reviews", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're subscribed. Welcome to Luigi Lube!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative overflow-hidden bg-ink pt-24">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 pb-16 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo className="text-2xl" iconClass="h-6 w-6" />
            <p className="mt-4 max-w-sm text-white/55">
              Premium mobile oil change service in {CONTACT.area}. We bring the shop to your driveway — punctual, professional and hassle-free.
            </p>
            <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-8 flex max-w-sm items-center gap-2 rounded-full border border-ink-soft bg-ink-surface p-1.5">
              <input
                data-testid="newsletter-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for offers"
                className="flex-1 bg-transparent px-4 py-2 text-sm text-white placeholder:text-white/30 outline-none"
              />
              <button data-testid="newsletter-submit" disabled={loading} className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink transition-colors hover:bg-gold-hover disabled:opacity-60">
                <Send className="h-4 w-4" strokeWidth={2} />
              </button>
            </form>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Explore</h4>
            <ul className="mt-5 space-y-3">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <button onClick={() => go(l.href)} className="text-white/65 transition-colors hover:text-gold">{l.label}</button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Contact</h4>
            <ul className="mt-5 space-y-3 text-white/65">
              <li><a href={CONTACT.phoneHref} className="flex items-center gap-2 hover:text-gold"><Phone className="h-4 w-4" strokeWidth={1.5} />{CONTACT.phone}</a></li>
              <li><a href={CONTACT.emailHref} className="flex items-center gap-2 hover:text-gold"><Mail className="h-4 w-4" strokeWidth={1.5} />{CONTACT.email}</a></li>
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4" strokeWidth={1.5} />{CONTACT.area}</li>
            </ul>
            <div className="mt-6 flex gap-3">
              <a href="#contact" onClick={(e) => { e.preventDefault(); go("#contact"); }} className="flex h-10 w-10 items-center justify-center rounded-full border border-ink-soft text-white/60 transition-colors hover:border-gold hover:text-gold"><Instagram className="h-4 w-4" strokeWidth={1.5} /></a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); go("#contact"); }} className="flex h-10 w-10 items-center justify-center rounded-full border border-ink-soft text-white/60 transition-colors hover:border-gold hover:text-gold"><Facebook className="h-4 w-4" strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-ink-soft py-8 text-sm text-white/40 md:flex-row">
          <span>© {new Date().getFullYear()} Luigi Lube. All rights reserved.</span>
          <span>Mobile Oil Change · {CONTACT.area}</span>
        </div>
      </div>

      <div className="pointer-events-none select-none overflow-hidden">
        <div className="whitespace-nowrap text-center font-display text-[18vw] font-semibold leading-none tracking-tighter text-white/[0.03]">
          LUIGI LUBE
        </div>
      </div>
    </footer>
  );
}
